/**
 * Merchant_payment.jsx
 * ESDRAS MERCHANT MARKETPLACE — Payment Handler Component
 * Used by: Merchant_checkout.jsx (embedded, not a standalone page)
 * Handles: Monnify NGN, Stripe International (scaffold), Esdras Wallet
 * Split logic is triggered via merchantSplitPayment cloud function post-payment
 *
 * NOTE: This component exports a reusable <MerchantPaymentButton /> for flexibility
 * and a full <Merchant_payment /> page for direct route use (/merchant/payment)
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD      = '#B8860B';
const DEEP_BLACK = '#050a0f';
const CARD_BG   = '#0a0f14';
const BORDER    = '#1a2631';

// ─── PAYMENT BUTTON COMPONENT (used inline in checkout) ──────────────────────
/**
 * MerchantPaymentButton
 * Props:
 *   method       : 'monnify' | 'stripe' | 'wallet'
 *   amount       : total amount in NGN (number)
 *   orderData    : { productId, merchantId, qty, buyerEmail, buyerName, buyerPhone, deliveryAddress, deliveryFee, comment }
 *   userId       : firebase uid (null for guests)
 *   walletBalance: user's current wallet balance (for wallet method)
 *   onSuccess    : (paymentReference, method) => void
 *   onError      : (error) => void
 *   disabled     : boolean
 */
export function MerchantPaymentButton({
  method = 'monnify',
  amount,
  orderData,
  userId,
  walletBalance = 0,
  onSuccess,
  onError,
  disabled = false,
}) {
  const functions = getFunctions();
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (disabled || loading) return;
    setLoading(true);
    try {
      if (method === 'monnify')  await payMonnify();
      if (method === 'stripe')   await payStripe();
      if (method === 'wallet')   await payWallet();
    } catch (err) {
      onError?.(err);
      toast.error(err.message || 'Payment failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Monnify ───────────────────────────────────────────────────────────────
  const payMonnify = () => new Promise((resolve, reject) => {
    if (!window.MonnifySDK) {
      reject(new Error('Monnify SDK not loaded. Please refresh the page.'));
      return;
    }
    const ref = `ESDRSMKT_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    window.MonnifySDK.initialize({
      amount,
      currency:            'NGN',
      reference:           ref,
      customerFullName:    orderData.buyerName,
      customerEmail:       orderData.buyerEmail,
      customerMobileNumber: orderData.buyerPhone,
      apiKey:              process.env.REACT_APP_MONNIFY_API_KEY    || 'MK_TEST_placeholder',
      contractCode:        process.env.REACT_APP_MONNIFY_CONTRACT_CODE || '626972837495',
      paymentDescription:  `Esdras Marketplace: ${orderData.productName || 'Product'} x${orderData.qty}`,
      metadata: {
        productId:   orderData.productId,
        merchantId:  orderData.merchantId,
        qty:         orderData.qty,
        buyerEmail:  orderData.buyerEmail,
        paymentType: 'MARKETPLACE_PURCHASE',
      },
      onComplete: (resp) => {
        if (resp.status === 'SUCCESS') {
          resolve(resp.paymentReference);
          onSuccess?.(resp.paymentReference, 'monnify');
        } else {
          reject(new Error('Payment was not completed.'));
        }
      },
      onClose: () => {
        setLoading(false);
        reject(new Error('Payment cancelled.'));
      },
    });
  });

  // ── Stripe (scaffold — keys to be added later) ─────────────────────────────
  const payStripe = async () => {
    const createStripeSession = httpsCallable(functions, 'merchantCreateStripeSession');
    const result = await createStripeSession({
      productId:       orderData.productId,
      merchantId:      orderData.merchantId,
      qty:             orderData.qty,
      buyerEmail:      orderData.buyerEmail,
      buyerName:       orderData.buyerName,
      buyerPhone:      orderData.buyerPhone,
      deliveryAddress: orderData.deliveryAddress,
      deliveryFee:     orderData.deliveryFee,
      grandTotal:      amount,
      comment:         orderData.comment,
    });
    if (result.data?.url) {
      // Redirect to Stripe Checkout page
      window.location.href = result.data.url;
    } else {
      throw new Error('Stripe checkout is not yet configured. Please use Monnify for NGN payments.');
    }
  };

  // ── Esdras Wallet ─────────────────────────────────────────────────────────
  const payWallet = async () => {
    if (!userId) throw new Error('You must be signed in to pay with Esdras Wallet.');
    if (walletBalance < amount) {
      throw new Error(`Insufficient balance. You have ₦${walletBalance.toLocaleString()}, need ₦${amount.toLocaleString()}.`);
    }
    const processWallet = httpsCallable(functions, 'merchantWalletPayment');
    const result = await processWallet({
      ...orderData,
      buyerUid:    userId,
      totalAmount: amount,
    });
    if (result.data?.success) {
      onSuccess?.(result.data.reference, 'wallet');
    } else {
      throw new Error(result.data?.message || 'Wallet payment failed.');
    }
  };

  const label = loading
    ? 'PROCESSING...'
    : `PAY ₦${Number(amount).toLocaleString()} via ${method.toUpperCase()}`;

  return (
    <button onClick={handlePay} disabled={disabled || loading}
      style={{
        background: disabled || loading ? '#2a2a2a' : GOLD,
        color: 'black', padding: '16px 24px', borderRadius: '50px',
        fontWeight: '900', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.9rem', letterSpacing: '1px', width: '100%',
        fontFamily: 'inherit', transition: 'all 0.2s',
        opacity: loading ? 0.7 : 1,
      }}>
      {label}
    </button>
  );
}

// ─── FULL PAGE (for /merchant/payment route — handles Stripe return + Wallet) ─
export default function Merchant_payment() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const functions = getFunctions();

  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [status,  setStatus]  = useState(null); // 'success' | 'error' | null
  const [message, setMessage] = useState('');

  // Handle Stripe success/cancel redirects: /merchant/payment?stripe=success&session_id=...
  const params          = new URLSearchParams(location.search);
  const stripeStatus    = params.get('stripe');
  const stripeSessionId = params.get('session_id');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!stripeStatus) { setLoading(false); return; }

    if (stripeStatus === 'success' && stripeSessionId) {
      handleStripeSuccess(stripeSessionId);
    } else if (stripeStatus === 'cancel') {
      setStatus('error');
      setMessage('Payment was cancelled. Your order has not been placed.');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [stripeStatus, stripeSessionId]);

  const handleStripeSuccess = async (sessionId) => {
    try {
      const confirmStripe = httpsCallable(functions, 'merchantConfirmStripePayment');
      const result = await confirmStripe({ sessionId });
      if (result.data?.success) {
        setStatus('success');
        setMessage(`Order confirmed! Order ID: ${result.data.orderId}`);
      } else {
        setStatus('error');
        setMessage('Payment received but order confirmation failed. Please contact support.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Could not confirm payment. Please contact support with your Stripe session ID.');
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={S.page}>
      <div style={S.centered}>
        <div style={S.spinner} />
        <p style={{ color: GOLD, letterSpacing: '2px', fontSize: '0.75rem', marginTop: '16px' }}>CONFIRMING PAYMENT...</p>
      </div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (status === 'success') return (
    <div style={S.page}>
      <div style={S.centered}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>✅</div>
          <h2 style={{ color: GOLD, letterSpacing: '2px' }}>PAYMENT CONFIRMED</h2>
          <p style={{ color: '#bbb', lineHeight: '1.7', marginTop: '10px' }}>{message}</p>
          <button onClick={() => navigate('/')} style={S.goldBtn}>BACK TO ESDRAS</button>
        </div>
      </div>
    </div>
  );

  if (status === 'error') return (
    <div style={S.page}>
      <div style={S.centered}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>❌</div>
          <h2 style={{ color: '#ff4d4d', letterSpacing: '2px' }}>PAYMENT ISSUE</h2>
          <p style={{ color: '#bbb', lineHeight: '1.7', marginTop: '10px' }}>{message}</p>
          <p style={{ color: '#555', fontSize: '0.8rem' }}>
            Contact <span style={{ color: GOLD }}>esdrastechnologiesinc@gmail.com</span> for help.
          </p>
          <button onClick={() => navigate('/')} style={S.goldBtn}>BACK TO HOME</button>
        </div>
      </div>
    </div>
  );

  // Default: no stripe params — explain payment methods
  return (
    <div style={S.page}>
      <div style={S.container}>
        <h1 style={S.pageTitle}>PAYMENT METHODS</h1>
        <div style={S.grid}>
          {/* Monnify */}
          <div style={S.pmCard}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🏦</div>
            <h3 style={{ color: GOLD, margin: '0 0 8px' }}>Monnify — NGN</h3>
            <p style={{ color: '#888', fontSize: '0.85rem', lineHeight: '1.6' }}>
              Pay in Nigerian Naira using bank transfer, USSD, card, or mobile money.
              Powered by Monnify by TeamApt — Nigeria's trusted payment infrastructure.
            </p>
            <div style={S.pmBadge}>✅ Active</div>
          </div>

          {/* Stripe */}
          <div style={S.pmCard}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>💳</div>
            <h3 style={{ color: '#a8dadc', margin: '0 0 8px' }}>Stripe — International</h3>
            <p style={{ color: '#888', fontSize: '0.85rem', lineHeight: '1.6' }}>
              Pay internationally using Visa, Mastercard, or American Express.
              Supports USD, GBP, EUR and other major currencies.
            </p>
            <div style={{ ...S.pmBadge, background: '#1a2631', color: '#a8dadc', borderColor: '#a8dadc44' }}>
              🔧 Coming Soon
            </div>
          </div>

          {/* Wallet */}
          <div style={S.pmCard}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>💰</div>
            <h3 style={{ color: '#4caf50', margin: '0 0 8px' }}>Esdras Wallet</h3>
            <p style={{ color: '#888', fontSize: '0.85rem', lineHeight: '1.6' }}>
              Esdras users with an active account wallet can pay instantly using their wallet balance.
              Fast, seamless, no redirects.
            </p>
            <div style={{ ...S.pmBadge, background: '#0a1f0a', color: '#4caf50', borderColor: '#4caf5044' }}>
              ✅ Active for Esdras Users
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <button onClick={() => navigate(-1)} style={S.outlineBtn}>← BACK</button>
        </div>
      </div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: DEEP_BLACK, color: 'white', fontFamily: "'Montserrat', 'Inter', sans-serif" },
  centered: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column' },
  container: { maxWidth: '900px', margin: '0 auto', padding: '50px 24px' },
  pageTitle: { color: GOLD, fontWeight: '900', letterSpacing: '4px', margin: '0 0 40px', fontSize: '1.4rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' },
  pmCard: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '18px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '4px' },
  pmBadge: { background: `${GOLD}15`, border: `1px solid ${GOLD}44`, color: GOLD, padding: '5px 14px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', display: 'inline-flex', alignSelf: 'flex-start', marginTop: '8px' },
  goldBtn: { background: GOLD, color: 'black', padding: '15px 30px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', marginTop: '20px', fontFamily: 'inherit' },
  outlineBtn: { background: 'transparent', color: GOLD, padding: '13px 30px', borderRadius: '50px', fontWeight: '700', border: `1.5px solid ${GOLD}`, cursor: 'pointer', fontSize: '0.88rem', fontFamily: 'inherit' },
  spinner: { width: '40px', height: '40px', border: `3px solid ${GOLD}22`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
